import React, { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Building2,
  MapPin,
  FileText,
  Droplets,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  Layers
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Admin Layout Component
 * 
 * Main layout wrapper for the admin panel with sidebar navigation,
 * header, and content area. Provides consistent navigation and
 * user interface across all admin pages.
 */
const AdminLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Navigation items - filter based on user role
  const allNavigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: Home,
      current: location.pathname === '/dashboard',
    },
    {
      name: 'Watersheds',
      href: '/watersheds',
      icon: Building2,
      current: location.pathname.startsWith('/watersheds'),
    },
    {
      name: 'Sites',
      href: '/sites',
      icon: MapPin,
      current: location.pathname.startsWith('/sites'),
    },
    {
      name: 'Points',
      href: '/points',
      icon: Layers,
      current: location.pathname.startsWith('/points'),
      allowedRoles: ['admin', 'super_admin', 'watershed_manager'],
    },
    {
      name: 'Submissions',
      href: '/submissions',
      icon: FileText,
      current: location.pathname.startsWith('/submissions'),
    },
    {
      name: 'Users',
      href: '/users',
      icon: Users,
      current: location.pathname.startsWith('/users'),
      allowedRoles: ['admin', 'super_admin'], // Hide from watershed_manager
    },
  ];

  // Filter navigation items based on user role
  const navigationItems = allNavigationItems.filter(item => {
    if (item.allowedRoles) {
      return item.allowedRoles.includes(user?.role);
    }
    return true; // Show items without role restrictions to everyone
  });

  const handleNavigation = (href) => {
    navigate(href);
    setSidebarOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="h-screen bg-secondary-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-secondary-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:flex flex-col h-full
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-6 bg-primary-600">
          <div className="flex items-center">
            <Droplets className="w-8 h-8 text-white" />
            <span className="ml-2 text-xl font-bold text-white">River Monitor</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:text-primary-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.name}
                onClick={() => handleNavigation(item.href)}
                className={`
                  sidebar-link w-full text-left
                  ${item.current 
                    ? 'sidebar-link-active' 
                    : 'sidebar-link-inactive'
                  }
                `}
              >
                <Icon className="w-5 h-5 mr-3" />
                {item.name}
              </button>
            );
          })}
        </nav>

        {/* User profile and logout */}
        <div className="p-4 border-t border-secondary-200 flex-shrink-0">
          <div className="flex items-center mb-4">
            <img
              className="w-10 h-10 rounded-full"
              src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Admin')}&background=92C1FF&color=fff`}
              alt={user?.name}
            />
            <div className="ml-3">
              <p className="text-sm font-medium text-secondary-900">
                {user?.name}
              </p>
              <p className="text-xs text-secondary-500">
                {user?.role === 'admin' ? 'System Administrator' : user?.role}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-left text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col h-full lg:pl-0 overflow-hidden">
        {/* Top header */}
        <header className="bg-primary-600 border-b border-secondary-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-white hover:text-primary-200"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Search bar
            <div className="flex-1 max-w-lg mx-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search farms, users, submissions..."
                  className="form-input pl-10 w-full"
                />
              </div>
            </div> */}

            {/* Header actions */}
            <div className="flex items-center space-x-4">
              {/* Notifications
              <button className="relative text-secondary-400 hover:text-secondary-500">
                <Bell className="w-6 h-6" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button> */}

              {/* User menu
              <div className="hidden sm:flex items-center">
                <img
                  className="w-8 h-8 rounded-full"
                  src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Admin')}&background=92C1FF&color=fff`}
                  alt={user?.name}
                />
                <span className="ml-2 text-sm font-medium text-secondary-700">
                  {user?.name}
                </span>
              </div> */}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;